import type { FaqEntry } from "@/lib/json-ld";

export const USE_CASES_FAQS: FaqEntry[] = [
  {
    q: "What are proxies used for?",
    a: "Proxies provide an intermediary network route for configured traffic. Businesses use them for legitimate workflows such as public-web data collection, localized testing, price monitoring, SEO monitoring, market research, ad verification, and brand protection.",
  },
  {
    q: "Do I need a proxy for web scraping?",
    a: "Not always. Start with a direct connection when it meets the workflow's requirements. A proxy becomes useful when collection requires geographic coverage, multiple network routes, controlled rotation, or consistent sessions.",
  },
  {
    q: "When should I use residential instead of mobile proxies?",
    a: "Residential proxies usually fit global web-data collection, price monitoring, SEO monitoring, and market research. Mobile proxies are more appropriate when the workflow specifically needs a mobile-carrier network context, mobile testing, or mobile SERP observations.",
  },
  {
    q: "Does a proxy scrape or extract data?",
    a: "No. A proxy provides the network route. Your scraper, browser automation, API client, or extraction system remains responsible for requesting, parsing, validating, and storing data.",
  },
  {
    q: "Are proxy use cases legal?",
    a: "Proxy technology has many legitimate business uses, but legality and contractual permission depend on the activity, jurisdiction, data, and target. Users remain responsible for complying with applicable laws, contracts, website terms, privacy obligations, and Proxidize's Acceptable Use Policy.",
  },
];

export interface UseCaseSelectorRow {
  need: string;
  why: string;
  appropriate: string;
  guide: string;
  href: string;
}

export const USE_CASE_SELECTOR: UseCaseSelectorRow[] = [
  {
    need: "Collect public web data across countries",
    why: "Provides geographically distributed request routes",
    appropriate: "Residential proxies",
    guide: "Web Scraping",
    href: "/use-cases/web-scraping",
  },
  {
    need: "Collect material for AI or RAG pipelines",
    why: "Supports geographically diverse crawling at scale",
    appropriate: "Residential proxies; mobile where justified",
    guide: "AI and LLM Training",
    href: "/use-cases/ai-llm-training",
  },
  {
    need: "Operate an AI agent that browses websites",
    why: "Provides the network layer beneath browser or HTTP automation",
    appropriate: "Depends on target and geography",
    guide: "AI Agents",
    href: "/use-cases/ai-agents",
  },
  {
    need: "Compare localized prices and availability",
    why: "Allows repeatable observations from selected locations",
    appropriate: "Residential proxies",
    guide: "Price Monitoring",
    href: "/use-cases/price-monitoring",
  },
  {
    need: "Track rankings across countries or cities",
    why: "Helps establish a consistent geographic observation point",
    appropriate: "Residential; mobile for mobile SERPs",
    guide: "SEO Monitoring",
    href: "/use-cases/seo-monitoring",
  },
  {
    need: "Check how an advertisement appears by location",
    why: "Helps test regional delivery and landing pages",
    appropriate: "Residential or mobile",
    guide: "Ad Verification",
    href: "/use-cases/ad-verification",
  },
  {
    need: "Test country- or city-specific application behavior",
    why: "Provides a location-specific network route",
    appropriate: "Residential or mobile",
    guide: "Geolocation Testing",
    href: "/use-cases/localized-testing",
  },
  {
    need: "Research products and competitors by market",
    why: "Supports public-web research across regions",
    appropriate: "Residential proxies",
    guide: "Market Research",
    href: "/use-cases/market-research",
  },
  {
    need: "Monitor marketplaces for misuse or counterfeits",
    why: "Supports repeated regional observations",
    appropriate: "Residential proxies",
    guide: "Brand Protection",
    href: "/use-cases/brand-protection-monitoring",
  },
];

export interface ProxyTypeDecisionRow {
  requirement: string;
  residential: string;
  mobile: string;
}

export const PROXY_TYPE_DECISION: ProxyTypeDecisionRow[] = [
  {
    requirement: "Broad global coverage",
    residential: "Usually the stronger fit",
    mobile: "Depends on available mobile coverage",
  },
  {
    requirement: "Web scraping across many websites",
    residential: "Common starting option when direct access is insufficient",
    mobile: "Useful when a mobile network context is specifically required",
  },
  {
    requirement: "Price and marketplace monitoring",
    residential: "Strong fit",
    mobile: "Useful for mobile-specific experiences",
  },
  {
    requirement: "Local SEO and SERP monitoring",
    residential: "Country and city observations",
    mobile: "Mobile SERP and carrier-context observations",
  },
  {
    requirement: "Ad verification",
    residential: "Regional web advertising",
    mobile: "Mobile ads and mobile landing experiences",
  },
  {
    requirement: "Long, related browser actions",
    residential: "Sticky session",
    mobile: "Sticky or individually assigned mobile route",
  },
  {
    requirement: "Independent high-volume requests",
    residential: "Rotating access",
    mobile: "Rotating mobile pool",
  },
  {
    requirement: "Small task that works directly",
    residential: "A proxy may not be needed",
    mobile: "A proxy may not be needed",
  },
];

export interface ProxyRoleRow {
  component: string;
  responsibility: string;
}

export const PROXY_ROLE_TABLE: ProxyRoleRow[] = [
  {
    component: "Proxy",
    responsibility: "Provides the exit network and supported location/session behavior",
  },
  {
    component: "Scraper or browser automation",
    responsibility: "Opens pages and performs actions",
  },
  {
    component: "Parser or extraction model",
    responsibility: "Converts responses into structured information",
  },
  {
    component: "Validation layer",
    responsibility: "Determines whether the returned result is usable",
  },
  {
    component: "Storage or CRM",
    responsibility: "Retains the evidence and makes it available to the team",
  },
];

export interface BuyingFactor {
  title: string;
  body: string;
}

export const BUYING_FACTORS: BuyingFactor[] = [
  {
    title: "Geography",
    body: "Is a country sufficient, or does the workflow require city, ISP, ASN, or carrier targeting?",
  },
  {
    title: "Session behavior",
    body: "Are requests independent, or do several actions need the same IP?",
  },
  {
    title: "Application",
    body: "Is the workflow using an HTTP client, scraper, full browser, or external platform?",
  },
  {
    title: "Billing",
    body: "Is the workload better suited to bandwidth-based access or an individually assigned proxy?",
  },
  {
    title: "Validation",
    body: "How will the team verify location, session consistency, returned content, and data quality?",
  },
];
